import {
  DT_STEP_MS,
  LOG_VERSION,
  MAX_DT_MS,
  PAUSE_GRANULARITY_S,
  type KeystrokeLog,
  type LogOp,
  type OpKind,
} from './types'

export interface RecordInput {
  readonly from: number
  readonly to: number
  readonly text: string
  readonly kind?: OpKind
}

export interface RecorderOptions {
  /** Monotonic clock in milliseconds. */
  readonly now: () => number
  readonly assisted?: boolean
  /** Epoch seconds. Defaults to the wall clock at creation. */
  readonly createdAt?: number
}

export interface Recorder {
  record(input: RecordInput): LogOp
  ops(): readonly LogOp[]
  snapshot(): KeystrokeLog
  reset(): void
}

export interface QuantizedDt {
  readonly dt: number
  readonly pause?: number
}

/** Rounds a real gap to the log's timing resolution and caps it, keeping only a coarse pause length. */
export function quantizeDt(realMs: number): QuantizedDt {
  if (!Number.isFinite(realMs) || realMs <= 0) return { dt: 0 }
  const rounded = Math.round(realMs / DT_STEP_MS) * DT_STEP_MS
  if (rounded <= MAX_DT_MS) return { dt: rounded }
  const pauseSeconds = Math.round(realMs / 1000 / PAUSE_GRANULARITY_S) * PAUSE_GRANULARITY_S
  return { dt: MAX_DT_MS, pause: Math.max(PAUSE_GRANULARITY_S, pauseSeconds) }
}

export function classifyInsertion(text: string, assisted: boolean): OpKind {
  const chars = Array.from(text)
  if (chars.length <= 1 || text.trim() === '') return 'typed'
  return assisted ? 'assisted' : 'stamp'
}

export function createRecorder(options: RecorderOptions): Recorder {
  const assisted = options.assisted ?? false
  const createdAt = options.createdAt ?? Math.floor(Date.now() / 1000)
  let ops: readonly LogOp[] = []
  let lastAt: number | undefined

  const record = (input: RecordInput): LogOp => {
    const at = options.now()
    const gap = lastAt === undefined ? 0 : at - lastAt
    lastAt = at
    const kind = input.kind ?? classifyInsertion(input.text, assisted)
    const op: LogOp = { ...quantizeDt(gap), from: input.from, to: input.to, text: input.text, kind }
    ops = [...ops, op]
    return op
  }

  return {
    record,
    ops: () => [...ops],
    snapshot: () => ({ version: LOG_VERSION, createdAt, assisted, ops: [...ops] }),
    reset: () => {
      ops = []
      lastAt = undefined
    },
  }
}
